import { _getLogger } from '@doxyme/analytics';
import { Specs, attachHandler } from '@doxyme/extensions-common';
import ExtensionCapability from '../../capability';
import type ExtensionInstance from '../../instance';

type Constraints = Specs.FileService.Constraints;
type RequestUploadLinksPayload = Specs.FileService.RequestUploadLinksPayload;
type GetFileMetadataPayload = Specs.FileService.GetFileMetadataPayload;
type DeleteFilesPayload = Specs.FileService.DeleteFilesPayload;
type UploadLinksResultPayload = Specs.FileService.UploadLinksResultPayload;
type FileMetadataResultPayload = Specs.FileService.FileMetadataResultPayload;
type DeleteResultPayload = Specs.FileService.DeleteResultPayload;
type ServiceUrlResult = Specs.FileService.ServiceUrlResult;
type UploadStatusPayload = Specs.FileService.UploadStatusPayload;

const { FileService } = Specs;

const logger = _getLogger('fileService', {
  component: 'extensions',
  lib: 'bridge',
});

export type RequestUploadLinksHandler = (
  payload: RequestUploadLinksPayload,
) => Promise<UploadLinksResultPayload>;

export type GetFileMetadataHandler = (
  payload: GetFileMetadataPayload,
) => Promise<FileMetadataResultPayload>;

export type DeleteFilesHandler = (
  payload: DeleteFilesPayload,
) => Promise<DeleteResultPayload>;

export type GetServiceUrlHandler = () => Promise<ServiceUrlResult>;

/**
 * Provides extensions with programmatic access to the File Hosting Service
 * for uploading, downloading metadata, and deleting files. The host application
 * registers handlers that execute the actual API calls and return results.
 * The capability takes care of notifying the requesting extension instance.
 */
export default class FileServiceCapability extends ExtensionCapability<Constraints> {
  static readonly id = FileService.id;

  protected readonly requestUploadLinksHandlers: RequestUploadLinksHandler[] =
    [];
  protected readonly getFileMetadataHandlers: GetFileMetadataHandler[] = [];
  protected readonly deleteFilesHandlers: DeleteFilesHandler[] = [];
  protected readonly getServiceUrlHandlers: GetServiceUrlHandler[] = [];

  constructor() {
    super(FileService);

    this.handlers.push(
      [
        FileService.Actions.RequestUploadLinks,
        this.handleRequestUploadLinks.bind(this),
      ],
      [
        FileService.Actions.GetFileMetadata,
        this.handleGetFileMetadata.bind(this),
      ],
      [FileService.Actions.DeleteFiles, this.handleDeleteFiles.bind(this)],
      [FileService.Actions.GetServiceUrl, this.handleGetServiceUrl.bind(this)],
    );
  }

  private async handleRequestUploadLinks(
    payload: RequestUploadLinksPayload,
    instance: ExtensionInstance<Constraints>,
  ): Promise<void> {
    try {
      let result: UploadLinksResultPayload = [];
      for (const handler of this.requestUploadLinksHandlers) {
        result = await handler(payload);
      }
      instance.notify([FileService.Events.UploadLinksResult, result]);
    } catch (error) {
      const errorMessage = this.#getErrorMessage(
        error,
        'Failed to get upload links',
      );
      logger.error(
        errorMessage,
        this.#getLoggerProps(instance, 'requestUploadLinks', error),
      );
      instance.notify([
        FileService.Events.UploadLinksResult,
        payload.files.map(({ fileName }) => ({ fileName, errorMessage })),
      ]);
    }
  }

  private async handleGetFileMetadata(
    payload: GetFileMetadataPayload,
    instance: ExtensionInstance<Constraints>,
  ): Promise<void> {
    try {
      let result: FileMetadataResultPayload = [];
      for (const handler of this.getFileMetadataHandlers) {
        result = await handler(payload);
      }
      instance.notify([FileService.Events.FileMetadataResult, result]);
    } catch (error) {
      const errorMessage = this.#getErrorMessage(
        error,
        'Failed to get file metadata',
      );
      logger.error(
        errorMessage,
        this.#getLoggerProps(instance, 'getFileMetadata', error),
      );
      instance.notify([
        FileService.Events.FileMetadataResult,
        payload.fileUuids.map((uuid) => ({ uuid, errorMessage })),
      ]);
    }
  }

  private async handleDeleteFiles(
    payload: DeleteFilesPayload,
    instance: ExtensionInstance<Constraints>,
  ): Promise<void> {
    try {
      let result: DeleteResultPayload = [];
      for (const handler of this.deleteFilesHandlers) {
        result = await handler(payload);
      }
      instance.notify([FileService.Events.DeleteResult, result]);
    } catch (error) {
      const errorMessage = this.#getErrorMessage(
        error,
        'Failed to delete files',
      );
      logger.error(
        errorMessage,
        this.#getLoggerProps(instance, 'deleteFiles', error),
      );
      instance.notify([
        FileService.Events.DeleteResult,
        payload.fileUuids.map((fileUuid) => ({
          fileUuid,
          isSuccess: false,
          errorMessage,
        })),
      ]);
    }
  }

  private async handleGetServiceUrl(
    _payload: never,
    instance: ExtensionInstance<Constraints>,
  ): Promise<void> {
    try {
      let result: ServiceUrlResult | undefined;
      for (const handler of this.getServiceUrlHandlers) {
        result = await handler();
      }
      if (result) {
        instance.notify([FileService.Events.ServiceUrlResult, result]);
      }
    } catch (error) {
      const errorMessage = this.#getErrorMessage(
        error,
        'Failed to get service URL',
      );
      logger.error(
        errorMessage,
        this.#getLoggerProps(instance, 'getServiceUrl', error),
      );
      instance.notify([FileService.Events.ServiceUrlResult, { errorMessage }]);
    }
  }

  onRequestUploadLinks(handler: RequestUploadLinksHandler): () => void {
    return attachHandler(this.requestUploadLinksHandlers, handler);
  }

  onGetFileMetadata(handler: GetFileMetadataHandler): () => void {
    return attachHandler(this.getFileMetadataHandlers, handler);
  }

  onDeleteFiles(handler: DeleteFilesHandler): () => void {
    return attachHandler(this.deleteFilesHandlers, handler);
  }

  onGetServiceUrl(handler: GetServiceUrlHandler): () => void {
    return attachHandler(this.getServiceUrlHandlers, handler);
  }

  /**
   * Forward a realtime upload-status callback to all extension instances.
   * Called by the host when a `fileServiceUpload` realtime message arrives.
   */
  notifyUploadStatus(payload: UploadStatusPayload): void {
    this.notifyAll([FileService.Events.UploadStatus, payload]);
  }

  #getErrorMessage(error: unknown, fallback: string): string {
    return (error as { message?: string })?.message ?? fallback;
  }

  #getLoggerProps(
    instance: ExtensionInstance<Constraints>,
    action: string,
    error?: unknown,
  ) {
    return {
      manifest: { id: instance.manifest.id, name: instance.manifest.name },
      action,
      error,
    };
  }
}
